import React from "react";
import { Link } from "react-router-dom";
import { TIERS } from "@/lib/wordLimits";
import { useAuth } from "@/context/AuthContext";

export default function PricingPage() {
  const { user } = useAuth();
  const minorNeedsConsent =
    user?.is_minor && user?.guardian_consent_status !== "granted";
  return (
    <div className="max-w-6xl mx-auto px-6 py-16 fade-in" data-testid="pricing-page">
      <div className="max-w-2xl mb-12">
        <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-a)] font-medium">
          Pricing
        </p>
        <h1 className="font-serif text-4xl md:text-5xl text-[color:var(--text-primary)] mt-2 mb-4">
          Built around fair word limits.
        </h1>
        <p className="text-[color:var(--text-muted)] leading-relaxed">
          Every tier gets the same 400-word Initial Story. After that, each
          plan increases the room you have to respond. Payment provider is
          configurable — Stripe, PayPal, PayFast or Paystack can be wired once
          finalised.
        </p>
      </div>

      {minorNeedsConsent && (
        <div
          className="mb-8 rounded-2xl border border-yellow-300 bg-yellow-50 text-yellow-900 p-5"
          data-testid="pricing-minor-consent-notice"
        >
          <div className="font-medium">Guardian consent needed for upgrades</div>
          <p className="text-sm mt-1">
            You're aged 13–17. Free use of FairResolver is open to you, but
            paid upgrades require guardian consent. Head to your{" "}
            <Link to="/dashboard" className="underline font-medium">
              dashboard
            </Link>{" "}
            to send your guardian a consent link.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {TIERS.map((t) => (
          <div
            key={t.key}
            className={`relative rounded-2xl border bg-white p-6 flex flex-col ${
              t.highlight
                ? "border-[color:var(--participant-a)] ring-1 ring-[color:var(--participant-a)]/40"
                : "border-[color:var(--line)]"
            }`}
            data-testid={`pricing-card-${t.key}`}
          >
            {t.highlight && (
              <span className="absolute -top-3 left-5 text-xs px-3 py-1 rounded-full bg-[color:var(--participant-a)] text-white">
                Featured
              </span>
            )}
            <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
              {t.subtitle}
            </div>
            <div className="font-serif text-2xl mt-1">{t.name}</div>
            <div className="text-xl font-semibold mt-1">{t.price}</div>
            <ul className="mt-5 space-y-2 text-sm text-[color:var(--text-primary)] flex-1">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--participant-a)]" />
                  {b}
                </li>
              ))}
            </ul>
            <button
              type="button"
              data-testid={`tier-select-${t.key}`}
              className={`mt-6 w-full px-4 py-2.5 rounded-full text-sm font-semibold border transition ${
                t.highlight
                  ? "bg-[color:var(--participant-a)] text-white border-transparent hover:opacity-90"
                  : "bg-white text-[color:var(--text-primary)] border-[color:var(--line)] hover:bg-[color:var(--surface-sage)]"
              }`}
              onClick={() => {
                // Placeholder: payment provider is configurable
                window.location.assign("/dashboard?upgrade=" + t.key);
              }}
            >
              {t.cta}
            </button>
          </div>
        ))}
      </div>

      <div
        className="mt-10 rounded-xl bg-[color:var(--surface-sage)] border border-[color:var(--line)] p-5 text-sm text-[color:var(--text-primary)]"
        data-testid="payment-placeholder-banner"
      >
        <b>Payment provider:</b> a configurable placeholder is in place.
        Admins can switch to <b>Stripe, PayPal, PayFast, or Paystack</b> once
        the business decision is finalised — no migration of accounts
        required.
      </div>

      <div className="mt-6">
        <Link
          to="/register"
          className="text-sm font-medium text-[color:var(--participant-a)] hover:underline"
          data-testid="pricing-cta"
        >
          Create a free account to begin →
        </Link>
      </div>
    </div>
  );
}
